import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { ShoppingListModal } from "../components/ShoppingListModal";

interface DashboardData {
  stats: { jarsOnShelf: number; stepsDueThisWeek: number; litresResting: number; forksOfYourRecipes: number };
  inProgress: {
    id: string;
    name: string;
    dayNumber: number;
    targetDays: number;
    percentComplete: number;
    isDue: boolean;
    nextStep: { stepNumber: number; title: string; body: string; dueDate: string } | null;
  }[];
  lowStock: { name: string; quantity_value: number; quantity_unit: string }[];
  feed: { verb: string; summary: string; created_at: string; actor_name: string; recipe_title: string | null; recipe_id: string | null }[];
}

export function Dashboard() {
  const [data, setData] = useState<DashboardData | null>(null);
  const [listOpen, setListOpen] = useState(false);
  const navigate = useNavigate();

  async function load() {
    const d = await api.get<DashboardData>("/dashboard");
    setData(d);
  }

  useEffect(() => {
    load();
  }, []);

  async function completeStep(batchId: string, stepNumber: number) {
    await api.post(`/batches/${batchId}/steps/${stepNumber}/complete`, { status: "on_time" });
    load();
  }

  async function pushStep(batchId: string, stepNumber: number, days: number) {
    await api.post(`/batches/${batchId}/steps/${stepNumber}/push`, { days });
    load();
  }

  if (!data) return <main className="page">Loading your bench…</main>;

  const dateLabel = new Date().toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" });
  const headline =
    data.stats.stepsDueThisWeek === 0
      ? "Nothing needs you right now."
      : `${numberWord(data.stats.stepsDueThisWeek)} ${data.stats.stepsDueThisWeek === 1 ? "jar wants" : "jars want"} something from you.`;

  return (
    <main className="page page-narrow">
      <div className="page-header">
        <div>
          <h6 className="kicker">{dateLabel} · Your bench</h6>
          <h1 className="page-title page-title-lg">{headline}</h1>
        </div>
      </div>

      <hr className="hr" style={{ margin: "34px 0 0" }} />

      <div className="stat-grid">
        <div className="stat-cell">
          <p className="stat-value">{data.stats.jarsOnShelf}</p>
          <p className="stat-label">Jars on the shelf</p>
        </div>
        <div className="stat-cell">
          <p className="stat-value">{data.stats.stepsDueThisWeek}</p>
          <p className="stat-label">Steps due this week</p>
        </div>
        <div className="stat-cell">
          <p className="stat-value">{data.stats.litresResting}L</p>
          <p className="stat-label">Resting, all vessels</p>
        </div>
        <div className="stat-cell">
          <p className="stat-value">{data.stats.forksOfYourRecipes}</p>
          <p className="stat-label">Forks of your recipes</p>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) 340px", gap: 56, marginTop: 40, alignItems: "start" }}>
        <section>
          <div className="spread" style={{ borderBottom: "2px solid var(--color-divider)", paddingBottom: 12 }}>
            <h2 style={{ margin: 0, fontSize: 22, letterSpacing: "-0.015em" }}>In progress</h2>
            <Link to="/batches" style={{ fontSize: 13 }}>
              Open the batch log
            </Link>
          </div>

          {data.inProgress.length === 0 && (
            <p className="muted" style={{ padding: "24px 0" }}>
              Nothing brewing yet. <Link to="/recipes">Browse recipes</Link> to start your first jar.
            </p>
          )}

          <div className="flex-col">
            {data.inProgress.map((b) => (
              <article key={b.id} className="list-row" style={{ gridTemplateColumns: "44px minmax(0,1fr) 150px" }}>
                <span style={{ fontFamily: "var(--font-heading)", fontWeight: 800, fontSize: 13, paddingTop: 3 }} className="tabular">
                  {String(b.dayNumber).padStart(2, "0")}
                </span>
                <div style={{ minWidth: 0 }}>
                  <h3 style={{ margin: 0, fontSize: 24, letterSpacing: "-0.018em" }}>{b.name}</h3>
                  <p className="muted" style={{ margin: "8px 0 0", fontSize: 13.5, lineHeight: 1.55 }}>
                    Day {b.dayNumber} of {b.targetDays}
                    {b.nextStep ? `. ${b.nextStep.body || b.nextStep.title}` : ". Nothing scheduled — just resting."}
                  </p>
                  <div className="flex-row" style={{ marginTop: 16 }}>
                    <div className="progress-track">
                      <div className={`progress-fill${b.isDue ? "" : " neutral"}`} style={{ width: `${b.percentComplete}%` }} />
                    </div>
                    <span
                      style={{
                        fontSize: 11.5,
                        letterSpacing: "0.06em",
                        textTransform: "uppercase",
                        color: b.isDue ? "var(--color-accent-700)" : undefined,
                      }}
                      className={b.isDue ? undefined : "muted-strong"}
                    >
                      {b.isDue ? "Ready" : `${Math.max(b.targetDays - b.dayNumber, 0)} days left`}
                    </span>
                  </div>
                </div>
                <div className="flex-col" style={{ gap: 8, alignItems: "flex-start" }}>
                  {b.nextStep && (
                    <>
                      <button className="btn btn-primary btn-block" onClick={() => completeStep(b.id, b.nextStep!.stepNumber)}>
                        Log “{b.nextStep.title}”
                      </button>
                      <button className="btn btn-secondary btn-block" onClick={() => pushStep(b.id, b.nextStep!.stepNumber, 2)}>
                        Push 2 days
                      </button>
                    </>
                  )}
                  {!b.nextStep && (
                    <button className="btn btn-secondary btn-block" onClick={() => navigate(`/batches?open=${b.id}`)}>
                      Open the jar card
                    </button>
                  )}
                </div>
              </article>
            ))}
          </div>
        </section>

        <aside className="flex-col" style={{ gap: 34 }}>
          <div>
            <h6 className="eyebrow" style={{ marginBottom: 14 }}>
              Running low
            </h6>
            {data.lowStock.length === 0 && <p className="muted-strong" style={{ fontSize: 13 }}>Everything's stocked.</p>}
            <div className="flex-col">
              {data.lowStock.map((item) => (
                <div key={item.name} className="spread" style={{ padding: "11px 0", borderBottom: "1px solid var(--color-divider)", fontSize: 14 }}>
                  <span>{item.name}</span>
                  <span className="tabular" style={{ color: "var(--color-accent-700)" }}>
                    {item.quantity_value} {item.quantity_unit}
                  </span>
                </div>
              ))}
            </div>
            <button type="button" className="btn btn-ghost" style={{ marginTop: 10, paddingLeft: 0 }} onClick={() => setListOpen(true)}>
              Build a shopping list
            </button>
          </div>

          <div style={{ borderTop: "2px solid var(--color-divider)", paddingTop: 20 }}>
            <h6 className="eyebrow" style={{ marginBottom: 14 }}>
              From people you follow
            </h6>
            {data.feed.length === 0 && (
              <p className="muted-strong" style={{ fontSize: 13 }}>
                Follow other brewers from any recipe page to see their updates here.
              </p>
            )}
            {data.feed.map((event, i) => (
              <p key={i} style={{ margin: i === 0 ? 0 : "14px 0 0", fontSize: 14.5, lineHeight: 1.5 }}>
                <strong style={{ fontFamily: "var(--font-heading)", fontWeight: 800 }}>{event.actor_name}</strong>{" "}
                {describeEvent(event.verb)}{" "}
                {event.recipe_id ? <Link to={`/recipes/${event.recipe_id}`}>{event.recipe_title}</Link> : event.recipe_title}
              </p>
            ))}
          </div>
        </aside>
      </div>

      {listOpen && <ShoppingListModal onClose={() => setListOpen(false)} />}
    </main>
  );
}

function describeEvent(verb: string): string {
  if (verb === "forked") return "forked";
  if (verb === "published_version") return "pushed a new version of";
  if (verb === "started_batch") return "started brewing";
  return "updated";
}

function numberWord(n: number): string {
  const words = ["Zero", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten"];
  return words[n] ?? String(n);
}
